import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { useFlags } from "launchdarkly-react-client-sdk";
import IntegrationApi from "../apis/integrationApi";
import {
  buildDocumentationSections,
  type DocActionItem as ActionItem,
  type DocSection,
  type DocSubsection,
} from "../utils/theoDocumentationMerge";

/** TheoDocumentation: help-center capability index (navbar: Documentation). Parses static theo-capabilities.md and merges MSP Datto/NinjaOne Remote Session + Scripts from live integration config. */

type ParsedDocumentation = {
  title: string;
  intro: string[];
  sections: DocSection[];
};

type DynamicDocumentationResponse = {
  sections?: unknown;
};

const DocumentationLiveAvatar = React.lazy(
  () => import("../components/DocumentationLiveAvatar")
);

function getCapabilitiesMarkdownUrls(): string[] {
  const custom = process.env.REACT_APP_THEO_CAPABILITIES_URL;
  const urls: string[] = [];
  if (custom && custom.trim().length > 0) {
    urls.push(custom.trim());
  }

  const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  urls.push(`${base}/theo-capabilities.md`);
  urls.push("./theo-capabilities.md");
  urls.push("theo-capabilities.md");

  if (typeof window !== "undefined") {
    const pathDir = window.location.pathname.replace(/\/[^/]*$/, "/");
    urls.push(`${pathDir}theo-capabilities.md`);
  }

  return Array.from(new Set(urls));
}

const slugifyHeading = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const parseCapabilitiesMarkdown = (markdown: string): ParsedDocumentation => {
  const lines = markdown.split(/\r?\n/);

  let title = "Theo AI Capabilities";
  const intro: string[] = [];
  const sections: DocSection[] = [];

  let currentSection: DocSection | null = null;
  let currentSubsection: DocSubsection | null = null;
  let currentAction: ActionItem | null = null;

  const pushSection = () => {
    if (currentSection) {
      sections.push(currentSection);
    }
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) {
      continue;
    }

    if (line.startsWith("# ")) {
      title = line.replace(/^# /, "").trim();
      continue;
    }

    if (line.startsWith("## ")) {
      pushSection();
      currentSection = {
        id: slugifyHeading(line.replace(/^## /, "").trim()),
        title: line.replace(/^## /, "").trim(),
        subsections: [],
      };
      currentSubsection = null;
      currentAction = null;
      continue;
    }

    if (line.startsWith("### ")) {
      if (!currentSection) {
        continue;
      }

      currentSubsection = {
        id: slugifyHeading(`${currentSection.title}-${line.replace(/^### /, "").trim()}`),
        title: line.replace(/^### /, "").trim(),
        actions: [],
      };
      currentSection.subsections.push(currentSubsection);
      currentAction = null;
      continue;
    }

    const actionMatch = line.match(/^- \*\*(.+?):\*\*\s*(.+)$/);
    if (actionMatch && currentSubsection) {
      currentAction = {
        name: actionMatch[1].trim(),
        description: actionMatch[2].trim(),
      };
      currentSubsection.actions.push(currentAction);
      continue;
    }

    if (currentSubsection && currentAction) {
      currentAction.description = `${currentAction.description} ${line}`.trim();
      continue;
    }

    if (!currentSection) {
      intro.push(line);
    }
  }

  pushSection();

  return { title, intro, sections };
};

const normalizeDynamicDocumentationSections = (sections: unknown): DocSection[] => {
  if (!Array.isArray(sections)) {
    return [];
  }

  return sections
    .map((section) => {
      if (!section || typeof section !== "object") {
        return null;
      }
      const rawSection = section as Record<string, any>;
      const title = String(rawSection.title || "").trim();
      if (!title) {
        return null;
      }

      const subsections = Array.isArray(rawSection.subsections)
        ? rawSection.subsections
            .map((subsection: any) => {
              const subsectionTitle = String(subsection?.title || "").trim();
              if (!subsectionTitle) {
                return null;
              }

              const actions = Array.isArray(subsection?.actions)
                ? subsection.actions
                    .map((action: any) => {
                      const name = String(action?.name || "").trim();
                      const description = String(action?.description || "").trim();
                      if (!name || !description) {
                        return null;
                      }
                      return { name, description };
                    })
                    .filter(Boolean) as ActionItem[]
                : [];

              if (actions.length === 0) {
                return null;
              }

              return {
                id: String(subsection?.id || slugifyHeading(`${title}-${subsectionTitle}`)).trim(),
                title: subsectionTitle,
                actions,
              };
            })
            .filter(Boolean) as DocSubsection[]
        : [];

      if (subsections.length === 0) {
        return null;
      }

      return {
        id: String(rawSection.id || slugifyHeading(title)).trim(),
        title,
        subsections,
      };
    })
    .filter(Boolean) as DocSection[];
};

const looksLikeHtmlDocument = (content: string): boolean => {
  const trimmed = content.trim().toLowerCase();
  return (
    trimmed.startsWith("<!doctype html") ||
    trimmed.startsWith("<html") ||
    (trimmed.includes("<head") && trimmed.includes("<body"))
  );
};

/** Application areas hidden from the in-app capability index (per product request). */
const EXCLUDED_DOC_SECTION_IDS = new Set([
  "slack",
]);

const DOC_SECTION_SEARCH_KEYWORDS: Record<string, string[]> = {
  datto: ["rmm", "datto rmm"],
  ninjaone: ["rmm", "ninja rmm", "ninjaone rmm"],
  "n-able": ["rmm", "nable", "n-central", "n-able rmm"],
  superops: ["rmm", "superops rmm"],
};

const sectionMatchesSearch = (section: DocSection, query: string): boolean => {
  const keywords = DOC_SECTION_SEARCH_KEYWORDS[section.id] || [];
  const haystack = [section.title, ...keywords].join(" ").toLowerCase();
  return haystack.includes(query);
};

const TheoDocumentation: React.FC = () => {
  const flags = useFlags();
  const avatarEnabled = Boolean((flags as Record<string, unknown>).avatar);
  const [markdown, setMarkdown] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [dynamicDocumentationSections, setDynamicDocumentationSections] = useState<DocSection[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeAnchorId, setActiveAnchorId] = useState("");

  const handlePrintDocumentation = useCallback(() => {
    document.body.classList.add("theo-doc-print-active");
    let fallbackId: ReturnType<typeof setTimeout>;
    const cleanup = () => {
      document.body.classList.remove("theo-doc-print-active");
      window.removeEventListener("afterprint", cleanup);
      if (fallbackId) {
        clearTimeout(fallbackId);
      }
    };
    window.addEventListener("afterprint", cleanup);
    // Keep print mode active long enough for print-preview pagination on large docs.
    fallbackId = setTimeout(cleanup, 120000);
    requestAnimationFrame(() => {
      window.print();
    });
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadDocumentation = async () => {
      try {
        setIsLoading(true);
        setError("");
        const candidateUrls = getCapabilitiesMarkdownUrls();
        let content = "";
        let loaded = false;

        for (const url of candidateUrls) {
          try {
            const response = await fetch(url, { cache: "no-store" });
            if (!response.ok) continue;
            const body = await response.text();
            if (!body || looksLikeHtmlDocument(body)) continue;
            content = body;
            loaded = true;
            break;
          } catch (_) {
            // Try next candidate URL.
          }
        }

        if (!loaded) {
          throw new Error(
            "Theo capabilities markdown was not found in deployed static assets. Please verify theo-capabilities.md is deployed."
          );
        }

        if (isMounted) {
          setMarkdown(content);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err?.message || "Unable to load Theo documentation.");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadDocumentation();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadDynamicDocumentation = async () => {
      try {
        const integrationApi = new IntegrationApi();
        const response = await integrationApi.getDocumentationRmmCapabilities() as DynamicDocumentationResponse;
        if (isMounted) {
          setDynamicDocumentationSections(normalizeDynamicDocumentationSections(response?.sections));
        }
      } catch (err) {
        if (isMounted) {
          setDynamicDocumentationSections([]);
        }
      }
    };

    loadDynamicDocumentation();
    return () => {
      isMounted = false;
    };
  }, []);

  const parsedDocumentation = useMemo(
    () => parseCapabilitiesMarkdown(markdown),
    [markdown]
  );

  const documentationSections = useMemo(
    () =>
      buildDocumentationSections(
        parsedDocumentation.sections.filter(
          (section) => !EXCLUDED_DOC_SECTION_IDS.has(section.id)
        ),
        dynamicDocumentationSections
      ),
    [parsedDocumentation.sections, dynamicDocumentationSections]
  );

  const totalActionCount = useMemo(
    () =>
      documentationSections.reduce(
        (sectionCount, section) =>
          sectionCount +
          section.subsections.reduce(
            (subsectionCount, subsection) =>
              subsectionCount + subsection.actions.length,
            0
          ),
        0
      ),
    [documentationSections]
  );

  const filteredSections = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return documentationSections;
    }

    return documentationSections
      .map((section) => {
        const sectionMatches = sectionMatchesSearch(section, query);
        const subsections = section.subsections
          .map((subsection) => {
            const subsectionMatches = subsection.title.toLowerCase().includes(query);
            const filteredActions =
              sectionMatches || subsectionMatches
                ? subsection.actions
                : subsection.actions.filter(
                    (action) =>
                      action.name.toLowerCase().includes(query) ||
                      action.description.toLowerCase().includes(query)
                  );

            if (!sectionMatches && !subsectionMatches && filteredActions.length === 0) {
              return null;
            }

            return {
              ...subsection,
              actions: filteredActions,
            };
          })
          .filter(Boolean) as DocSubsection[];

        if (!sectionMatches && subsections.length === 0) {
          return null;
        }

        return {
          ...section,
          subsections: sectionMatches ? section.subsections : subsections,
        };
      })
      .filter(Boolean) as DocSection[];
  }, [documentationSections, searchQuery]);

  const filteredActionCount = useMemo(
    () =>
      filteredSections.reduce(
        (sectionCount, section) =>
          sectionCount +
          section.subsections.reduce(
            (subsectionCount, subsection) =>
              subsectionCount + subsection.actions.length,
            0
          ),
        0
      ),
    [filteredSections]
  );

  const anchorIds = useMemo(
    () =>
      filteredSections.flatMap((section) => [
        section.id,
        ...section.subsections.map((subsection) => subsection.id),
      ]),
    [filteredSections]
  );

  useEffect(() => {
    if (!filteredSections.length) {
      setActiveAnchorId("");
      return;
    }

    setActiveAnchorId((current) =>
      current && anchorIds.includes(current) ? current : filteredSections[0].id
    );
  }, [anchorIds, filteredSections]);

  useEffect(() => {
    if (!anchorIds.length) {
      return;
    }

    const observedNodes = anchorIds
      .map((id) => document.getElementById(id))
      .filter(Boolean) as HTMLElement[];

    if (observedNodes.length === 0) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort(
            (entryA, entryB) =>
              Math.abs(entryA.boundingClientRect.top) -
              Math.abs(entryB.boundingClientRect.top)
          );

        if (visibleEntries[0]?.target?.id) {
          setActiveAnchorId(visibleEntries[0].target.id);
        }
      },
      {
        root: null,
        rootMargin: "-18% 0px -64% 0px",
        threshold: [0, 0.15, 0.4],
      }
    );

    observedNodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [anchorIds]);

  const activeSection = useMemo(
    () =>
      filteredSections.find(
        (section) =>
          section.id === activeAnchorId ||
          section.subsections.some((subsection) => subsection.id === activeAnchorId)
      ) || filteredSections[0],
    [activeAnchorId, filteredSections]
  );

  const scrollToAnchor = (id: string) => {
    const element = document.getElementById(id);
    if (!element) {
      return;
    }

    element.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    setActiveAnchorId(id);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          background: "#f8fafc",
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress sx={{ color: "#705d4a" }} />
          <Typography sx={{ fontFamily: "var(--theo-font-sans)", color: "#64748b" }}>
            Loading Theo documentation...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          width: "100%",
          p: { xs: 3, md: 5 },
          display: "flex",
          justifyContent: "center",
          background: "#f8fafc",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: 720,
            width: "100%",
            p: 4,
            borderRadius: 2,
            border: "1px solid #f0f0f0",
            background: "#fff",
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 700, color: "#000", mb: 1 }}
          >
            Documentation unavailable
          </Typography>
          <Typography sx={{ fontFamily: "var(--theo-font-sans)", color: "#64748b" }}>
            {error}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        px: { xs: 2, md: 3 },
        py: { xs: 2, md: 3 },
        background: "#f8fafc",
      }}
    >
      <Box
        className="theo-doc-print-grid"
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "280px minmax(0, 1fr)",
            xl: "280px minmax(0, 1fr) 220px",
          },
          gap: 3,
          alignItems: "start",
          width: "100%",
        }}
      >
        <Box
          className="theo-doc-print-hide"
          sx={{
            position: { lg: "sticky" },
            top: { lg: 24 },
            display: "flex",
            flexDirection: "column",
            gap: 2,
            maxHeight: { lg: "calc(100vh - 140px)" },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: "1px solid #f0f0f0",
              background: "#fff",
              p: 2,
            }}
          >
            <Stack spacing={2}>
              <Box>
                <Typography
                  sx={{
                    fontFamily: "var(--theo-font-sans)",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "#64748b",
                    mb: 0.75,
                  }}
                >
                  Documentation
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "var(--theo-font-sans)",
                    fontSize: 20,
                    lineHeight: 1.2,
                    fontWeight: 700,
                    color: "#000",
                  }}
                >
                  Capability index
                </Typography>
              </Box>

              <TextField
                fullWidth
                size="small"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search actions, apps, or areas"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon sx={{ color: "#64748b", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchQuery("")}
                        sx={{ color: "#64748b" }}
                      >
                        <ClearRoundedIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                    background: "#f8fafc",
                    fontFamily: "var(--theo-font-sans)",
                  },
                }}
              />

              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip
                  size="small"
                  icon={<MenuBookRoundedIcon sx={{ color: "#705d4a !important" }} />}
                  label={`${documentationSections.length} areas`}
                  sx={{
                    background: "#705D4B21",
                    color: "#000",
                    fontFamily: "var(--theo-font-sans)",
                    fontWeight: 600,
                    border: "1px solid #f0f0f0",
                  }}
                />
                <Chip
                  size="small"
                  icon={<AutoStoriesOutlinedIcon sx={{ color: "#705d4a !important" }} />}
                  label={`${totalActionCount} actions`}
                  sx={{
                    background: "#fff",
                    color: "#64748b",
                    fontFamily: "var(--theo-font-sans)",
                    fontWeight: 600,
                    border: "1px solid #f0f0f0",
                  }}
                />
              </Stack>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: "1px solid #f0f0f0",
              background: "#fff",
              p: 1,
              overflow: "auto",
            }}
          >
            <Stack spacing={0.5}>
              {filteredSections.map((section) => {
                const sectionActionCount = section.subsections.reduce(
                  (count, subsection) => count + subsection.actions.length,
                  0
                );
                const isSectionActive =
                  activeAnchorId === section.id ||
                  section.subsections.some((subsection) => subsection.id === activeAnchorId);

                return (
                  <Box key={section.id}>
                    <Box
                      onClick={() => scrollToAnchor(section.id)}
                      sx={{
                        borderRadius: 1,
                        px: 1.25,
                        py: 0.85,
                        cursor: "pointer",
                        background: isSectionActive ? "#705D4B21" : "transparent",
                        borderRight: isSectionActive ? "3px solid #705d4a" : "3px solid transparent",
                        transition: "background 150ms ease",
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Typography
                          sx={{
                            fontFamily: "var(--theo-font-sans)",
                            fontWeight: 700,
                            fontSize: 14,
                            color: isSectionActive ? "#000" : "#64748b",
                          }}
                        >
                          {section.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "var(--theo-font-sans)",
                            fontSize: 12,
                            color: "#8790ac",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {sectionActionCount}
                        </Typography>
                      </Stack>
                    </Box>

                    <Stack spacing={0.25} sx={{ mt: 0.25, mb: 0.75, pl: 1.5 }}>
                      {section.subsections.map((subsection) => {
                        const isSubsectionActive = subsection.id === activeAnchorId;

                        return (
                          <Box
                            key={subsection.id}
                            onClick={() => scrollToAnchor(subsection.id)}
                            sx={{
                              cursor: "pointer",
                              borderLeft: "2px solid #f0f0f0",
                              pl: 1.25,
                              py: 0.45,
                              color: isSubsectionActive ? "#000" : "#666",
                              bgcolor: isSubsectionActive ? "#f8fafc" : "transparent",
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: "var(--theo-font-sans)",
                                fontSize: 13,
                                fontWeight: isSubsectionActive ? 700 : 500,
                              }}
                            >
                              {subsection.title}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                );
              })}

              {filteredSections.length === 0 && (
                <Typography
                  sx={{
                    px: 1.5,
                    py: 2,
                    fontFamily: "var(--theo-font-sans)",
                    fontSize: 14,
                    color: "#64748b",
                  }}
                >
                  No documentation matches this search.
                </Typography>
              )}
            </Stack>
          </Paper>
        </Box>

        <Box className="theo-doc-print-main-column" sx={{ minWidth: 0 }}>
          {avatarEnabled ? (
            <Box className="theo-doc-print-hide" sx={{ mb: 3 }}>
              <React.Suspense fallback={null}>
                <DocumentationLiveAvatar />
              </React.Suspense>
            </Box>
          ) : null}

          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: "1px solid #f0f0f0",
              background: "#fff",
              p: { xs: 2, md: 3 },
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                mb: 3,
                pb: 2.5,
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", md: "flex-start" }}
                  spacing={2}
                >
                  <Box sx={{ maxWidth: 760, flex: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: "var(--theo-font-sans)",
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: "#64748b",
                        mb: 1,
                      }}
                    >
                      Capability reference
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "var(--theo-font-sans)",
                        fontSize: { xs: 26, md: 32 },
                        lineHeight: 1.15,
                        fontWeight: 700,
                        color: "#000",
                        mb: 1,
                      }}
                    >
                      {parsedDocumentation.title}
                    </Typography>
                    <Stack spacing={1}>
                      {parsedDocumentation.intro.map((paragraph, idx) => (
                        <Typography
                          key={`intro-${idx}-${paragraph.slice(0, 48)}`}
                          sx={{
                            fontFamily: "var(--theo-font-sans)",
                            fontSize: 15,
                            lineHeight: 1.6,
                            color: "#64748b",
                            maxWidth: 760,
                          }}
                        >
                          {paragraph}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    useFlexGap
                    alignItems={{ xs: "stretch", sm: "flex-start" }}
                    sx={{ minWidth: { md: 200 }, flexShrink: 0 }}
                  >
                    <Button
                      type="button"
                      variant="outlined"
                      size="medium"
                      className="theo-doc-print-hide"
                      startIcon={<PrintRoundedIcon />}
                      onClick={handlePrintDocumentation}
                      sx={{
                        fontFamily: "var(--theo-font-sans)",
                        fontWeight: 600,
                        borderColor: "#e2e8f0",
                        color: "#334155",
                        whiteSpace: "nowrap",
                        "&:hover": { borderColor: "#705d4a", color: "#705d4a" },
                      }}
                    >
                      Print
                    </Button>
                    <Stack
                      direction={{ xs: "row", md: "column" }}
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                      className="theo-doc-print-hide"
                    >
                      <Chip
                        label={`${filteredSections.length} visible areas`}
                        sx={{
                          background: "#705D4B21",
                          color: "#000",
                          fontFamily: "var(--theo-font-sans)",
                          fontWeight: 600,
                          border: "1px solid #f0f0f0",
                        }}
                      />
                      <Chip
                        label={`${filteredActionCount} visible actions`}
                        sx={{
                          background: "#fff",
                          color: "#64748b",
                          fontFamily: "var(--theo-font-sans)",
                          fontWeight: 600,
                          border: "1px solid #f0f0f0",
                        }}
                      />
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </Box>

            <Stack spacing={3.5}>
              {filteredSections.map((section) => {
                const sectionActionCount = section.subsections.reduce(
                  (count, subsection) => count + subsection.actions.length,
                  0
                );

                return (
                  <Box
                    key={section.id}
                    id={section.id}
                    sx={{ scrollMarginTop: "96px" }}
                  >
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      justifyContent="space-between"
                      spacing={1.5}
                      sx={{ mb: 1.75 }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontFamily: "var(--theo-font-sans)",
                            fontSize: { xs: 22, md: 26 },
                            lineHeight: 1.15,
                            fontWeight: 700,
                            color: "#000",
                            mb: 0.5,
                          }}
                        >
                          {section.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "var(--theo-font-sans)",
                            fontSize: 13,
                            lineHeight: 1.5,
                            color: "#64748b",
                          }}
                        >
                          {section.subsections.length} subsections · {sectionActionCount} actions
                        </Typography>
                      </Box>

                      <Chip
                        label={`${sectionActionCount} actions`}
                        size="small"
                        sx={{
                          alignSelf: { xs: "flex-start", md: "center" },
                          background: "#705D4B21",
                          color: "#000",
                          fontFamily: "var(--theo-font-sans)",
                          fontWeight: 600,
                          border: "1px solid #f0f0f0",
                        }}
                      />
                    </Stack>

                    <Stack spacing={1.5}>
                      {section.subsections.map((subsection) => (
                        <Paper
                          key={subsection.id}
                          id={subsection.id}
                          elevation={0}
                          sx={{
                            scrollMarginTop: "112px",
                            borderRadius: 2,
                            border: "1px solid #f0f0f0",
                            background: "#fff",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              px: { xs: 1.5, md: 2 },
                              py: 1.25,
                              background: "#f8fafc",
                              borderBottom: "1px solid #f0f0f0",
                            }}
                          >
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              spacing={1}
                            >
                              <Typography
                                sx={{
                                  fontFamily: "var(--theo-font-sans)",
                                  fontSize: { xs: 16, md: 17 },
                                  lineHeight: 1.2,
                                  fontWeight: 700,
                                  color: "#000",
                                }}
                              >
                                {subsection.title}
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: "var(--theo-font-sans)",
                                  fontSize: 12,
                                  color: "#8790ac",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {subsection.actions.length} actions
                              </Typography>
                            </Stack>
                          </Box>

                          <Stack divider={<Divider flexItem />} spacing={0}>
                            {subsection.actions.map((action) => (
                              <Box
                                key={`${subsection.id}-${action.name}`}
                                sx={{
                                  px: { xs: 1.5, md: 2 },
                                  py: { xs: 0.85, md: 0.65 },
                                  transition: "background 120ms ease",
                                  "&:hover": {
                                    background: "#fafafa",
                                  },
                                }}
                              >
                                <Stack
                                  direction={{ xs: "column", sm: "row" }}
                                  spacing={{ xs: 0.35, sm: 2 }}
                                  alignItems={{ xs: "flex-start", sm: "flex-start" }}
                                >
                                  <Typography
                                    sx={{
                                      fontFamily: "var(--theo-font-sans)",
                                      fontSize: 14,
                                      lineHeight: 1.35,
                                      fontWeight: 700,
                                      color: "#000",
                                      flex: { sm: "0 0 220px" },
                                      minWidth: { sm: 200 },
                                      maxWidth: { sm: 280 },
                                    }}
                                  >
                                    {action.name}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontFamily: "var(--theo-font-sans)",
                                      fontSize: 13,
                                      lineHeight: 1.45,
                                      color: "#64748b",
                                      flex: 1,
                                      minWidth: 0,
                                    }}
                                  >
                                    {action.description}
                                  </Typography>
                                </Stack>
                              </Box>
                            ))}
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                );
              })}

              {filteredSections.length === 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 2,
                    border: "1px solid #f0f0f0",
                    background: "#fff",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "var(--theo-font-sans)",
                      fontSize: 20,
                      fontWeight: 700,
                      color: "#000",
                      mb: 1,
                    }}
                  >
                    No matching capabilities
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "var(--theo-font-sans)",
                      color: "#64748b",
                      lineHeight: 1.7,
                    }}
                  >
                    Try a broader search like <strong>M365</strong>, <strong>ticket</strong>,
                    <strong>search</strong>, or <strong>password</strong>.
                  </Typography>
                </Paper>
              )}
            </Stack>
          </Paper>
        </Box>

        <Box
          className="theo-doc-print-hide"
          sx={{
            display: { xs: "none", xl: "block" },
            position: "sticky",
            top: 24,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: "1px solid #f0f0f0",
              background: "#fff",
              p: 2,
            }}
          >
            <Typography
              sx={{
                fontFamily: "var(--theo-font-sans)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#64748b",
                mb: 1,
              }}
            >
              On this page
            </Typography>

            {activeSection ? (
              <Stack spacing={1}>
                <Typography
                  sx={{
                    fontFamily: "var(--theo-font-sans)",
                    fontSize: 16,
                    fontWeight: 700,
                    color: "#000",
                  }}
                >
                  {activeSection.title}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "var(--theo-font-sans)",
                    fontSize: 13,
                    color: "#64748b",
                    lineHeight: 1.6,
                    mb: 0.5,
                  }}
                >
                  {activeSection.subsections.length} subsections in view.
                </Typography>

                <Stack spacing={0.5}>
                  {activeSection.subsections.map((subsection) => (
                    <Box
                      key={subsection.id}
                      onClick={() => scrollToAnchor(subsection.id)}
                      sx={{
                        px: 1,
                        py: 0.65,
                        borderRadius: 1,
                        cursor: "pointer",
                        background:
                          activeAnchorId === subsection.id ? "#705D4B21" : "transparent",
                        borderRight:
                          activeAnchorId === subsection.id
                            ? "3px solid #705d4a"
                            : "3px solid transparent",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "var(--theo-font-sans)",
                          fontSize: 13,
                          fontWeight: activeAnchorId === subsection.id ? 700 : 500,
                          color: activeAnchorId === subsection.id ? "#000" : "#64748b",
                        }}
                      >
                        {subsection.title}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Stack>
            ) : (
              <Typography
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  fontSize: 13,
                  color: "#64748b",
                }}
              >
                Use the left navigation to jump across application areas.
              </Typography>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default TheoDocumentation;
